import axios from 'axios';
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { formatCurrency, formatPercent } from "@/lib/config";

const CustomTooltip = ({ active, payload, label, isPercent }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="font-medium text-slate-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={entry.id || entry._id || index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {isPercent ? `${entry.value.toFixed(1)}%` : formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ChangeIndicator = ({ current, previous }) => {
  if (!previous || previous === 0) return <Minus className="w-4 h-4 text-slate-400" />;
  const change = ((current - previous) / Math.abs(previous)) * 100;
  if (change > 0) {
    return (
      <span className="flex items-center gap-1 text-emerald-600 text-sm">
        <TrendingUp className="w-4 w-4" />
        +{change.toFixed(1)}%
      </span>
    );
  } else if (change < 0) {
    return (
      <span className="flex items-center gap-1 text-red-500 text-sm">
        <TrendingDown className="w-4 h-4" />
        {change.toFixed(1)}%
      </span>
    );
  }
  return <Minus className="w-4 h-4 text-slate-400" />;
};

export function TrendModal({ 
  open, 
  onClose, 
  title, 
  kpiKey,
  departement = "GLOBAL",
  isPercent = false,
  chartType = "bar" // "bar" ou "line"
}) {
  const [loading, setLoading] = useState(true);
  const [mensuelle2024, setMensuelle2024] = useState([]);
  const [mensuelle2025, setMensuelle2025] = useState([]);
  const [trimestrielle2024, setTrimestrielle2024] = useState([]);
  const [trimestrielle2025, setTrimestrielle2025] = useState([]);
  const [annuelle, setAnnuelle] = useState([]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, departement, kpiKey]);

  const loadData = async () => {
    setLoading(true);
    try {
      const baseUrl = (window.__API_URL__ || '') + '/api';
      const [m2024, m2025, t2024, t2025, yearly] = await Promise.all([
        axios.get(`${baseUrl}/analyse/mensuelle/2024?departement=${departement}`).then(r => r.data),
        axios.get(`${baseUrl}/analyse/mensuelle/2025?departement=${departement}`).then(r => r.data),
        axios.get(`${baseUrl}/analyse/trimestrielle/2024?departement=${departement}`).then(r => r.data),
        axios.get(`${baseUrl}/analyse/trimestrielle/2025?departement=${departement}`).then(r => r.data),
        axios.get(`${baseUrl}/analyse/annuelle?departement=${departement}`).then(r => r.data)
      ]);
      setMensuelle2024(m2024);
      setMensuelle2025(m2025);
      setTrimestrielle2024(t2024);
      setTrimestrielle2025(t2025);
      setAnnuelle(yearly);
    } catch (error) {
      console.error("Error loading trend data:", error);
    }
    setLoading(false);
  };

  // Extraire la valeur du KPI selon la clé
  const extractValue = (data, key) => {
    // Mapping des clés KPI vers les clés de données backend
    const keyMapping = {
      // Revenus - tous mappent vers "revenus" dans l'API
      'revenus_total': 'revenus',
      'revenus_bancaire': 'revenus',
      'revenus_cash': 'revenus',
      'revenus_objectif': 'revenus',
      
      // Dépenses - toutes mappent vers "depenses" dans l'API
      'depenses_total': 'depenses',
      'frais_variables': 'depenses',
      'frais_fixes': 'depenses',
      'cout_moyen': 'depenses',
      
      // Rentabilité - utilisent les clés directes
      'marge_brute': 'marge_brute',
      'marge_nette': 'marge_nette',
      'profit': 'profit',
      'depenses': 'depenses',
      
      // Pour les clés qui n'ont pas de mapping, on retourne 0 car elles ne sont pas dans l'API historique
      // (comme pct_heures_terrain, soumissions, etc. qui nécessiteraient des calculs plus complexes)
    };
    
    const mappedKey = keyMapping[key];
    
    // Si on a un mapping, utiliser cette clé
    if (mappedKey) {
      return data[mappedKey] || 0;
    }
    
    // Sinon, essayer la clé directement (pour les KPIs avancés qui pourraient être ajoutés plus tard)
    return data[key] || 0;
  };

  const monthlyData = mensuelle2024.map((m, idx) => ({
    mois: m.mois_nom,
    value_2024: extractValue(m, kpiKey),
    value_2025: extractValue(mensuelle2025[idx] || {}, kpiKey)
  }));

  const quarterlyData = trimestrielle2024.map((t, idx) => ({
    trimestre: t.trimestre,
    value_2024: extractValue(t, kpiKey),
    value_2025: extractValue(trimestrielle2025[idx] || {}, kpiKey)
  }));

  const total2024 = monthlyData.reduce((sum, m) => sum + m.value_2024, 0);
  const total2025 = monthlyData.reduce((sum, m) => sum + m.value_2025, 0);

  const Chart = chartType === "line" ? LineChart : BarChart;
  const ChartElement = chartType === "line" ? Line : Bar;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{title}</DialogTitle>
          <p className="text-sm text-slate-500">Évolution dans le temps - {departement}</p>
        </DialogHeader>

        {/* Résumé */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500">2024</p>
              <p className="text-2xl font-bold text-slate-900">
                {isPercent ? `${(total2024/12).toFixed(1)}%` : formatCurrency(total2024)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500">2025 (YTD)</p>
              <p className="text-2xl font-bold text-emerald-600">
                {isPercent ? `${(total2025/12).toFixed(1)}%` : formatCurrency(total2025)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500">Variation</p>
              <ChangeIndicator current={total2025} previous={total2024} />
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="mom">
          <TabsList className="mb-4">
            <TabsTrigger value="mom">Mois vs Mois (MoM)</TabsTrigger>
            <TabsTrigger value="qoq">Trimestre vs Trimestre (QoQ)</TabsTrigger>
            <TabsTrigger value="yoy">Année vs Année (YoY)</TabsTrigger>
          </TabsList>

          <TabsContent value="mom">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Évolution Mensuelle - 2024 vs 2025</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <Chart data={monthlyData}>
                      <XAxis dataKey="mois" tick={{ fontSize: 10 }} tickFormatter={(v) => v.substring(0, 3)} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => isPercent ? `${v}%` : `${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip isPercent={isPercent} />} />
                      <Legend />
                      {chartType === "line" ? (
                        <>
                          <Line type="monotone" dataKey="value_2024" name="2024" stroke="#94A3B8" strokeWidth={2} />
                          <Line type="monotone" dataKey="value_2025" name="2025" stroke="#10B981" strokeWidth={2} />
                        </>
                      ) : (
                        <>
                          <Bar dataKey="value_2024" name="2024" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="value_2025" name="2025" fill="#10B981" radius={[4, 4, 0, 0]} />
                        </>
                      )}
                    </Chart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Tableau détaillé */}
            <Card className="mt-4">
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Mois</th>
                        <th className="text-right py-2 px-3">2024</th>
                        <th className="text-right py-2 px-3">2025</th>
                        <th className="text-center py-2 px-3">Var.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.map((row, idx) => (
                        <tr key={row.id || row._id || idx} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3">{row.mois}</td>
                          <td className="text-right py-2 px-3">
                            {isPercent ? `${row.value_2024.toFixed(1)}%` : formatCurrency(row.value_2024)}
                          </td>
                          <td className="text-right py-2 px-3 font-semibold text-emerald-600">
                            {isPercent ? `${row.value_2025.toFixed(1)}%` : formatCurrency(row.value_2025)}
                          </td>
                          <td className="text-center py-2 px-3">
                            <ChangeIndicator current={row.value_2025} previous={row.value_2024} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="qoq">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {quarterlyData.map((q, idx) => {
                const q2025 = q.value_2025;
                const q2024 = q.value_2024;
                return (
                  <Card key={q.trimestre}>
                    <CardHeader>
                      <CardTitle className="text-base">{q.trimestre}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-slate-500">2024</p>
                          <p className="font-semibold">
                            {isPercent ? `${q2024.toFixed(1)}%` : formatCurrency(q2024)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">2025</p>
                          <p className="font-semibold text-emerald-600">
                            {isPercent ? `${q2025.toFixed(1)}%` : formatCurrency(q2025)}
                          </p>
                        </div>
                        <div className="pt-2 border-t">
                          <ChangeIndicator current={q2025} previous={q2024} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="yoy">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {annuelle.map((year, idx) => {
                const value = extractValue(year, kpiKey);
                const prevYear = annuelle[idx - 1];
                const prevValue = prevYear ? extractValue(prevYear, kpiKey) : null;
                
                return (
                  <Card key={year.annee}>
                    <CardHeader>
                      <CardTitle>Année {year.annee}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-slate-500">Valeur annuelle</p>
                          <p className="text-3xl font-bold text-emerald-600">
                            {isPercent ? `${value.toFixed(1)}%` : formatCurrency(value)}
                          </p>
                        </div>
                        {prevYear && (
                          <div className="bg-blue-50 p-3 rounded-lg flex justify-between items-center">
                            <span className="text-sm">vs {prevYear.annee}</span>
                            <ChangeIndicator current={value} previous={prevValue} />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
